/* eslint-disable react/prop-types */
import { useState } from 'react';
import { useFetchChargerOnlineTime } from './Hook/Index';
import {
  Table,
  Row,
  Col,
  Card,
  Empty,
  Alert,
  Grid,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import SharedFilters from './SharedFilters';
import PropTypes from 'prop-types';
import { useAuthStore } from '../../store/useAuthStore';
import ReportTrendChart from './ReportTrendChart';

const { useBreakpoint } = Grid;

export default function ChargerOnlineTimeTab({
  operatorName,
  operatorId,
  active = false,
}) {
  const { t } = useTranslation('reportPage');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [chargerId, setChargerId] = useState(null);
  const { user } = useAuthStore();

  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ]);
  const { data: response, isLoading } = useFetchChargerOnlineTime(
    {
      operatorId,
      chargerId: chargerId,
      fromDate: dateRange[0],
      toDate: dateRange[1],
    },
    { enabled: active }
  );

  const data = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response)
      ? response
      : response
        ? [response]
        : [];

  const totalPossibleHours = dateRange.length === 2
    ? dayjs(dateRange[1]).endOf('day').diff(dayjs(dateRange[0]).startOf('day'), 'hours') + 1
    : 0;

  const flattenedData = operatorId
    ? (data[0]?.chargers || []).map((charger) => ({
        serialNumber: charger.serialNumber,
        chargerName: charger.name,
        totalOnline: (charger.totalOnline || 0) / 60,
        onlinePercentage: (((charger.totalOnline || 0) / 60) / totalPossibleHours) * 100,
      }))
    : data.flatMap((operator) =>
        operator?.chargers?.map((charger) => ({
          operatorId: operator?.operatorId,
          operatorName: operator?.operatorDetails?.name || operator?.operatorId,
          serialNumber: charger?.serialNumber,
          chargerName: charger?.name,
          totalOnline: (charger?.totalOnline || 0) / 60,
          onlinePercentage: (((charger?.totalOnline || 0) / 60) / totalPossibleHours) * 100,
        }))
      );

  const onlineSeries = (response?.series || []).map((row) => ({
    ...row,
    hours: (row.totalOnline || 0) / 60,
  }));

  const totalOnlineTime = (flattenedData || []).reduce(
    (sum, record) => sum + (record?.totalOnline || 0),
    0
  );

  const handleExportCsv = () => {
    if (!flattenedData.length) return;

    const csvData = flattenedData.map((record) => ({
      OperatorName: record.operatorName || operatorName || t('noDataAvailable'),
      ChargerName: record.chargerName || t('noDataAvailable'),
      SerialNumber: record.serialNumber || t('noDataAvailable'),
      OnlineTime: `${(record.totalOnline || 0)?.toFixed(2)} hours`,
      OnlinePercentage: `${(record.onlinePercentage || 0)?.toFixed(2)}%`
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'ChargerOnlineTime.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    ...(operatorId
      ? []
      : [
          {
            title: t('operatorName'),
            dataIndex: 'operatorName',
            key: 'operatorName',
            align: 'center',
            width: isMobile ? 120 : 150,
            responsive: ['md'],
          },
        ]),
    {
      title: t('chargerName'),
      dataIndex: 'chargerName',
      key: 'chargerName',
      align: 'center',
      width: isMobile ? 120 : 150,
    },
    {
      title: t('serialNumber'),
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      align: 'center',
      width: isMobile ? 100 : 120,
      responsive: ['md'],
    },
    {
      title: t('onlineTime'),
      dataIndex: 'totalOnline',
      key: 'totalOnline',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (value) => `${value?.toFixed(2)} hours`,
      sorter: (a, b) => a.totalOnline - b.totalOnline,
    },
    {
      title: t('onlinePercentage'),
      dataIndex: 'onlinePercentage',
      key: 'onlinePercentage',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (value) => `${value?.toFixed(2)}%`,
      sorter: (a, b) => a.onlinePercentage - b.onlinePercentage,
    },
  ];

  const handleFiltersChange = (filters) => {
    setDateRange(filters.dateRange);
    setChargerId(filters.chargerId);
  };

  return (
    <div>
      {operatorId &&   (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
        <Alert
          message={t('dashboard:alertMessage', {
            operatorName,
          })}
          type="info"
          showIcon
          closable
          style={{
            marginBottom: 24,
            border: 'none',
            backgroundColor: '#d7e1dd',
          }}
        />
      )}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4">
        <SharedFilters
          operatorId={operatorId}
          onFiltersChange={handleFiltersChange}
          onExport={handleExportCsv}
          isLoading={isLoading}
          showRfidFilter={false}
        />
      </div>
      <Row gutter={isMobile ? 8 : 16} className="mb-4">
        <Col span={isMobile ? 12 : 6}>
          <Card size="small" title={t('totalOnlineTime')} loading={isLoading}>
            <div className="text-2xl text-center">
              {totalOnlineTime.toFixed(2)} hours
            </div>
          </Card>
        </Col>
      </Row>
      <ReportTrendChart
        title={t('chargerOnlineTimeTrend')}
        series={onlineSeries}
        loading={isLoading}
        yFormatter={(val) => `${val.toFixed(2)} h`}
        fields={[{ key: 'hours', label: t('onlineTime') }]}
      />
      <div className="overflow-x-auto mt-4">
        {flattenedData.length > 0 ? (
          <Table
            columns={columns}
            dataSource={flattenedData}
            loading={isLoading}
            scroll={{ x: isMobile ? 600 : 'max-content', y: isMobile ? 250 : 250 }}
            rowKey={(record) =>
              operatorId
                ? record?.serialNumber
                : `${record?.operatorId}-${record?.serialNumber}`
            }
            pagination={{ 
              position: ["bottomCenter"], 
              size: isMobile ? "small" : "default", 
              pageSize: isMobile ? 5 : 10, 
              showSizeChanger: false 
            }}
          />
        ) : (
          !isLoading && <Empty description={t('noDataAvailable')} />
        )}
      </div>
    </div>
  );
}

ChargerOnlineTimeTab.propTypes = {
  operatorName: PropTypes.string,
  operatorId: PropTypes.string,
  active: PropTypes.bool,
};
